import Constants from 'expo-constants';
import React, { useEffect, useRef } from 'react';
import { StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import WebView, { WebViewMessageEvent } from 'react-native-webview';

import { type CardData, PaymentProcessor, usePaymentMethodStore } from '..';
import { EncryptionConfig } from '../services/webview-encryption-service';

interface EncryptionWebViewProps {
  cardData: CardData;
}

/**
 * EncryptionWebView - Invisible WebView for secure card encryption
 *
 * This component:
 * 1. Renders an invisible WebView with processor-specific encryption scripts
 * 2. Injects JavaScript to trigger encryption
 * 3. Receives encrypted data via postMessage
 * 4. Calls onResult/onError callbacks
 */
export const EncryptionWebView = ({ cardData }: EncryptionWebViewProps) => {
  const webviewRef = useRef<WebView>(null);
  const encryptionService = usePaymentMethodStore(state => state.encryptionService);

  // Get pending encryption from the service
  const pendingEncryption = encryptionService?.getWebviewPendingEncryption();

  const processor = pendingEncryption?.processor;
  const orderId = pendingEncryption?.orderId || '';
  const config = pendingEncryption?.config;
  const onResult = pendingEncryption?.onResult;
  const onError = pendingEncryption?.onError;

  // Inject JavaScript to trigger encryption when ready
  useEffect(() => {
    if (!processor || !config) return;

    const timeout = setTimeout(() => {
      if (processor === PaymentProcessor.ORBITAL) {
        webviewRef.current?.injectJavaScript(`
          encrypt("${cardData.cardNumber}", "${cardData.cvv}", ${config.mode === 'eFPE'});
          true;
        `);
      } else {
        webviewRef.current?.injectJavaScript(`
          makeEProtectCall("${cardData.cardNumber}", "${cardData.cvv}");
          true;
        `);
      }
    }, 600); // Small delay to ensure WebView is ready 400 (worldpay) 600 (orbital) are the minimum working intervals

    return () => clearTimeout(timeout);
  }, [processor, cardData, config]);

  // Don't render if there's no pending encryption
  if (!pendingEncryption || !processor || !config || !onResult || !onError) {
    return null;
  }

  // Generate HTML based on processor
  const customHtml =
    processor === PaymentProcessor.ORBITAL ? generateOrbitalHtml() : generateWorldpayHtml(orderId);

  // Handle messages from WebView
  const messageHandler = (event: WebViewMessageEvent) => {
    try {
      const data = JSON.parse(event.nativeEvent.data);

      if ('error' in data) {
        onError(data);
      } else {
        // Transform data based on processor
        if (processor === PaymentProcessor.ORBITAL) {
          onResult({
            ...data,
            subscriberId: config.subscriberId,
            pieFormat: config.pieFormat,
          });
        } else {
          onResult({
            lowValueToken: data.paypageRegistrationId,
            type: data.type,
            bin: data.bin,
            last4: data.lastFour,
          });
        }
      }
    } catch (error) {
      onError({
        error: error instanceof Error ? error.message : 'Failed to parse encryption response',
      });
    }
  };

  return (
    <SafeAreaView style={styles.hidden}>
      <WebView
        ref={webviewRef}
        source={{ html: customHtml }}
        onMessage={messageHandler}
        androidLayerType="software"
        javaScriptEnabled={true}
        domStorageEnabled={true}
        onError={syntheticEvent => {
          const { nativeEvent } = syntheticEvent;
          onError({
            error: `WebView error: ${nativeEvent.description}`,
          });
        }}
        onHttpError={syntheticEvent => {
          const { nativeEvent } = syntheticEvent;
          onError({
            error: `HTTP error: ${nativeEvent.statusCode}`,
          });
        }}
      />
    </SafeAreaView>
  );
};

/**
 * Generate HTML for Orbital (Safetech) encryption
 */
function generateOrbitalHtml(): string {
  const config: EncryptionConfig['orbital'] =
    Constants.expoConfig?.extra?.eas?.US?.orbital || Constants.expoConfig?.extra?.eas?.CA?.orbital;

  const getKeyScriptUrl = `${config?.baseUrl}/pie/v1/${config?.pieFormat}${config?.subscriberId}/getkey.js`;
  const encryptionScriptUrl = `${config?.baseUrl}/pie/v1/encryption.js`;

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
    </head>
    <body>
      <script src="${getKeyScriptUrl}"></script>
      <script src="${encryptionScriptUrl}"></script>
      
      <script>
        // Validate card number using MOD 10 checksum
        async function validate(cardNumber) {
          // Check MOD 10 digit, since PIE embedded encryption
          // requires that the MOD 10 checksum is valid.
          if (typeof ValidatePANChecksum !== 'function') {
            throw new Error("ValidatePANChecksum function not available");
          }
          
          if (!ValidatePANChecksum(cardNumber)) {
            throw new Error("PAN has invalid checksum");
          }
        }

        // Perform encryption and post results back
        async function encrypt(cardNumber, cvv, embed) {
          try {
            await validate(cardNumber);
            
            if (typeof ProtectPANandCVV !== 'function') {
              throw new Error("ProtectPANandCVV function not available");
            }
            
            const result = ProtectPANandCVV(cardNumber, cvv, !embed);
            
            if (!result || !Array.isArray(result) || result.length < 3) {
              throw new Error('Encryption failed - invalid result');
            }
            
            const [cryptCard, cryptCvv, integrityCheck] = result;
            
            if (!window.PIE || !window.PIE.key_id || !window.PIE.phase) {
              throw new Error('PIE data not available');
            }
            
            window.ReactNativeWebView.postMessage(JSON.stringify({
              cryptCard,
              cryptCvv,
              integrityCheck,
              keyId: window.PIE.key_id,
              phase: window.PIE.phase,
              mode: embed ? 'eFPE' : 'FPE',
            })); 
          } catch (err) {
            window.ReactNativeWebView.postMessage(JSON.stringify({
              error: err.message || 'Unknown encryption error'
            }));
          }
        }
      </script>
    </body>
    </html>
  `;
}

/**
 * Generate HTML for Worldpay (eProtect) encryption
 */
function generateWorldpayHtml(orderId: string): string {
  const config: EncryptionConfig['worldpay'] =
    Constants.expoConfig?.extra?.eas?.US?.worldpay ||
    Constants.expoConfig?.extra?.eas?.CA?.worldpay;

  const scriptUrl = `${config?.eProtectUrl}/eProtect/eProtect-api3.js`;
  const merchantOrderId = orderId?.slice(0, 20) || 'N/A'; // eProtect allows max of 20 characters
  const merchantReportGroup =
    `${Constants.expoConfig?.extra?.eas?.env}-FHS-${Constants.expoConfig?.extra?.eas?.buildRegion}`.toUpperCase();

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
    </head>
    <body>
      <script src="${scriptUrl}"></script>
      
      <script>
        function makeEProtectCall(cardNumber, cvv) {
          try {
            if (typeof eProtect !== 'function') {
              throw new Error('eProtect library not loaded');
            }
            
            const preRequest = {
              paypageId: '${config?.paypageId}',
              reportGroup: '${merchantReportGroup}',
              orderId: '${merchantOrderId}',
              id: '${merchantOrderId}',
              url: '${config?.eProtectUrl}'
            };
            
            const formFields = {
              accountNum: {
                value: cardNumber,
              },
              cvv2: {
                value: cvv,
              },
            };

            const timeout = 10000; // 10 second timeout
            
            new eProtect().sendToEprotect(
              preRequest,
              formFields,
              // Success callback
              function(data) {
                window.ReactNativeWebView.postMessage(JSON.stringify(data));
              },
              // Error callback
              function(error) {
                window.ReactNativeWebView.postMessage(JSON.stringify({
                  error: error.message || 'eProtect error',
                  ...error,
                }));
              },
              // Timeout callback
              function() {
                window.ReactNativeWebView.postMessage(JSON.stringify({
                  error: 'eProtect Request Timeout',
                }));
              },
              timeout
            );
          } catch (err) {
            window.ReactNativeWebView.postMessage(JSON.stringify({
              error: err.message || 'Unknown eProtect error'
            }));
          }
        }
      </script>
    </body>
    </html>
 `;
}

const styles = StyleSheet.create({
  hidden: {
    width: 0,
    height: 0,
    opacity: 0,
    position: 'absolute',
  },
});
